import React from 'react';
import {Autocomplete, Box, Divider, FormControlLabel, Stack, Switch, TextField, Typography} from '@mui/material';
import {FormSettings} from "crqlar-react-common/dist/models/forms/form";
import {useAllSectors} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {Sector} from "crqlar-react-common/dist/models/sector/sector.model";
import {useTranslation} from "react-i18next";

interface SettingsPanelProps {
    title: string;
    onTitleChange: (title: string) => void;
    settings: FormSettings;
    onSettingsUpdate: (settings: FormSettings) => void;
    disabled?: boolean;
    sectorId?: string;
    onSectorChange?: (sectorId: string | undefined) => void;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({
                                                         title,
                                                         onTitleChange,
                                                         settings,
                                                         onSettingsUpdate,
                                                         disabled,
                                                         sectorId,
                                                         onSectorChange
                                                     }) => {
    const {t} = useTranslation();
    const sectors = useAllSectors();
    const selectedSector = sectors.find(s => s.id === sectorId) ?? null;

    const handleChange = (key: keyof FormSettings) => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = event.target.type === 'checkbox'
            ? event.target.checked
            : event.target.value;

        onSettingsUpdate({
            ...settings,
            [key]: value,
        });
    };

    return (
        <Box sx={{p: 2, height: '100%', overflow: 'auto'}}>
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                {t('Settings')}
            </Typography>

            <Stack spacing={3}>
                <Box>
                    <Typography variant="subtitle2" gutterBottom>
                        {t('Name')}
                    </Typography>
                    <TextField
                        value={title}
                        onChange={event => onTitleChange(event.target.value)}
                        size="small"
                        fullWidth
                        required
                        disabled={disabled}
                    />
                </Box>

                <Divider/>

                <FormControlLabel
                    control={
                        <Switch
                            checked={settings.showProgressBar}
                            onChange={handleChange('showProgressBar')}
                            color="primary"
                            disabled={disabled}
                        />
                    }
                    label={t('Show progress bar')}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        '& .MuiFormControlLabel-label': {
                            fontSize: '0.875rem'
                        }
                    }}
                />

                <Divider/>

                <Box>
                    <Typography variant="subtitle2" gutterBottom>
                        {t('Linked Sector')}
                    </Typography>
                    <Autocomplete<Sector, false, false, false>
                        options={sectors}
                        getOptionLabel={(option) => option.name ?? ''}
                        value={selectedSector}
                        disabled={disabled}
                        onChange={(_event, newValue) => {
                            onSectorChange?.(newValue?.id ?? undefined);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                placeholder={t('No sector')}
                            />
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                    />
                </Box>
            </Stack>
        </Box>
    );
};

export default SettingsPanel;
